import { createApi } from "@reduxjs/toolkit/query/react";
import {
  difficultyCountObj,
  EditData,
} from "@src/pages/quiz/create-quiz-drawer/type&Validation";
import { baseQueryWithReauth } from "@src/utils/baseQuery";
import { APIResponse } from "./CommonApiSlice";
import { Pageable } from "@src/pages/batches/student-list-drawer/data.type";
import { PaginationType, Question, QuizOrTask } from "@src/types/CommonTypes";
import { Batch } from "@src/pages/batches/batch-base-component/data.type";
import { fileDownloadPayload, FileResponse } from "./TournamentsApiSlice";
import { PsychometricQuizResultResponse } from "@src/pages/task-manager/quiz/quiz-table/types";
import { AssessmentDashboardStudent } from "@src/pages/batches/batches-quiz-details/assessment-grade-distribution/types";

export interface AssessmentItem {
  id: string;
  taskName: string;
  duration: number;
  startDate: string;
  endDate: string;
  totalQuestions: number;
  totalMarks: number;
  scope: string;
  difficultyLevel: string;
  status: string;
  batchCount: number;
  studentCount: number;
}

export interface AssessmentListResponse extends PaginationType {
  content: AssessmentItem[];
}

export interface GetBatchAssessmentListApiResponse {
  statusCode: number;
  httpStatus: string;
  response: AssessmentListResponse;
}

export interface GetQuestionsByIDS {
  statusCode: number;
  httpStatus: string;
  response: Question[];
}

export interface QuizPayload {
  taskName?: string;
  duration?: number;
  passPercentage?: number | string;
  malPracticeCount?: number | string;
  startByDuration?: {
    days: number;
    hours: number;
    minutes: number;
  };
  endByDuration?: {
    days: number;
    hours: number;
    minutes: number;
  };
  parentId?: string;
  subjectId?: string;
  parentType?: string;
  description?: string;
  isShowResult?: boolean;
  isActualAnswer?: boolean;
  isAssignToBatch?: boolean;
  isShowToTrainer?: boolean;
  isShuffleQuestion?: boolean;
  parentName?: string;
  syllabusReference?: string;
  status?: string;
  difficultyLevel?: string;
  assessmentType?: string;
  sectionList?: SectionPayload[];
  organizationId?: string;
  startDate?: string;
  endDate?: string;
  allowOnMobile?: boolean;
  sectionSequence?: string;
  isZupfly?: boolean;
}

export interface SectionPayload {
  sectionName: string;
  sectionParentType?: string;
  sectionParentReference?: string;
  difficultyLevel?: string;
  marksType: string;
  questionList: QuestionPayload[];
  sectionType?: string;
  time?: number;
  hard?: number;
  medium?: number;
  questionCount?: number;
}

export interface QuestionPayload {
  questionId: string;
  versionId: string;
  marks: number;
  parentId: string;
  parentType: string;
}

export interface QuestionsForQuizPayload {
  parentId: string;
  count?: string | number;
  parentType: string;
  difficultyLevel?: string;
  questionSelectedType: string;
  pageNo: number;
  pageSize: number;
  userEmail?: string;
  batchId?: string;
  isTournament?: boolean;
  syllabusReference?: string;
  questionType?: string;
  questionFilter?: difficultyCountObj;
  traitIds?: string[];
  testType?: string;
  isZupfly?: boolean;
  paragraphId?: string;
}

export interface TrainerAssessmentApiResponse {
  statusCode: number;
  httpStatus: string;
  response: {
    content: EditData[];
  };
}

export interface GetQuestionsForAssessmentResponse extends APIResponse {
  response: {
    content: Question[];
    pageable: Pageable;
    totalPages: number;
    totalElements: number;
    last: boolean;
    size: number;
    number: number;
    numberOfElements: number;
    first: boolean;
    empty: boolean;
  };
}

export interface GetUnassingedAssessmentsResponse extends APIResponse {
  response: {
    assessmentId: string;
    assessmentName: string;
    scope: string;
  }[];
}

export interface GetAssessmentSummaryResponse extends APIResponse {
  response: QuizOrTask;
}
export interface GetAssessmentDashboardResponse extends APIResponse {
  response: AssessmentDashboardStudent[];
}

export interface GetAllAssessmentsResponse extends APIResponse {
  response: {
    content: QuizOrTask[];
  } & PaginationType;
}

export interface CreateAssessmentResponse extends APIResponse {
  response: QuizOrTask;
}

export interface EditeAssessmentResponse extends APIResponse {
  response: QuizOrTask;
}

export interface AssignAssessmentResponse extends APIResponse {
  response: string;
}

export interface ApproveRejectResponse extends APIResponse {
  response: QuizOrTask;
}

export interface GetBatchAssessmentResponse extends APIResponse {
  response: QuizOrTask;
}

export interface GetAssessmentDuplicateResponse extends APIResponse {
  response: boolean;
}

export interface GetTrainerAssessmentBatchListResponse extends APIResponse {
  response: { content: Batch[] };
}

export interface QuizUrlRepsonse extends APIResponse {
  response: {
    organizationId: string;
    organizationName: string;
    scope: "GLOBAL" | "ORGANIZATION";
    quizStudentStatus: string;
  };
}

type AdditionalDetailsValue =
  | string
  | number
  | boolean
  | Array<string | number | boolean>
  | null;

type AdditionalDetails = {
  [key: string]: AdditionalDetailsValue;
} | null;

type QuestionMetaData = {
  questionId: string | null;
  versionId: string | null;
  duration: number | null;
  status: string | null;
  questionNo: number | null;
  failedAttempts: number | null;
  totalAttempts: number | null;
  totalMarks: number | null;
  scoredMarks: number | null;
};

type SectionData = {
  sectionName: string;
  sectionTotalMarks: number;
  sectionScoredMarks: number;
  totalQuestions: number | null;
  questionsAttended: number | null;
  passedQuestions: number | null;
  subjectName: string;
  studentQuestionMetaDataList: {
    [questionId: string]: QuestionMetaData;
  };
};

export type StudentDataReport = {
  id: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  organization: string;
  subOrganization: string | null;
  branch: string | null;
  quizId: string;
  totalMarks: number;
  totalScore: number;
  questionsAttended: number;
  totalQuestions: number;
  totalTime: number;
  additionalDetails: AdditionalDetails;
  studentSectionDataList: SectionData[];
};

type Content = { content: StudentDataReport[] } & PaginationType;

export type QuizReportResponse = {
  statusCode: number;
  httpStatus: string;
  response: {
    passPercentage: number | null;
    avgScore: number | null;
    totalStudents: number | null;
    studentQuizResults: Content;
  };
};

export type QuizTableStructure = {
  [key: string]:
    | string
    | number
    | boolean
    | null
    | undefined
    | any[]
    | Record<string, any>;
};

export interface GetQuizzesMonthlyCountRespose extends APIResponse {
  response: { monthYear: string; count: number }[];
}

export interface GetPsychometricQuizResultResponse extends APIResponse {
  response: PsychometricQuizResultResponse;
}

export const AssessmentApiSlice = createApi({
  reducerPath: "assessmentApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["getAllAssessments", "getQuizCount"],
  endpoints: (builder) => ({
    getAllAssessment: builder.query<
      GetAllAssessmentsResponse,
      {
        parentId: string;
        pageNo: number;
        pageSize: number;
        organizationId: string;
      }
    >({
      query: ({ parentId, pageNo, pageSize, organizationId }) => ({
        url: `assessment_template/get-assessmentTemplates-parent?parentId=${parentId}&pageNo=${pageNo}&pageSize=${pageSize}&organizationId=${organizationId}`,
        method: "GET",
      }),
      providesTags: ["getAllAssessments"],
    }),
    getAssessmentSummary: builder.query<
      GetAssessmentSummaryResponse,
      { assessmentId: string }
    >({
      query: ({ assessmentId }) => ({
        url: `assessment_template?assessmentTemplateId=${assessmentId}`,
        method: "GET",
      }),
    }),
    getAssessmentDashboardForAssessment: builder.query<
      GetAssessmentDashboardResponse,
      { assessmentId: string; batchId: string }
    >({
      query: ({ assessmentId, batchId }) => ({
        url: `assessment/get-assessment-dashboard?assessmentId=${assessmentId}&batchId=${batchId}`,
        method: "GET",
      }),
    }),
    createAssessment: builder.mutation<
      CreateAssessmentResponse,
      { payload: QuizPayload; userRole: string; batchId?: string }
    >({
      query: ({ payload, userRole, batchId }) => ({
        url:
          userRole == "TRAINER" || userRole == "SUPER_TRAINER"
            ? `assessment/create-assessment-trainer?batchId=${batchId}`
            : `assessment_template`,
        method: "POST",
        body: payload,
      }),
    }),
    assignAssessment: builder.mutation<
      AssignAssessmentResponse,
      { assessmentId: string; payload: QuizPayload }
    >({
      query: ({ assessmentId, payload }) => ({
        url: `assessment_template/assign-assessment?assessmentTemplateId=${assessmentId}`,
        method: "POST",
        body: payload,
      }),
    }),
    getUnassignedAssessment: builder.query<
      GetUnassingedAssessmentsResponse,
      {
        syllabusId: string;
        organizationId: string;
        batchId: string | null;
      }
    >({
      query: ({ syllabusId, batchId, organizationId }) => ({
        url: `assessment_template/get-assessments-to-assign?organizationId=${organizationId}&syllabusId=${syllabusId}${
          batchId ? `&batchId=${batchId}` : ""
        }`,

        method: "GET",
      }),
    }),
    updateAssessment: builder.mutation<
      EditeAssessmentResponse,
      { payload: QuizPayload; assessmentId: string | number }
    >({
      query: ({ payload, assessmentId }) => ({
        url: `assessment_template?assessmentTemplateId=${assessmentId}`,
        method: "PUT",
        body: payload,
      }),
    }),
    updateAssessmentTrainer: builder.mutation<
      EditeAssessmentResponse,
      { payload: QuizPayload; assessmentId: string | number; batchId: string }
    >({
      query: ({ payload, assessmentId, batchId }) => ({
        url: `assessment?assessmentId=${assessmentId}&batchId=${batchId}`,
        method: "PUT",
        body: payload,
      }),
    }),
    updateMalPractice: builder.mutation<
      any,
      {
        resourceId: string;
        type: string;
        malPracticeCount: string;
        batchId?: string;
      }
    >({
      query: ({ resourceId, type, malPracticeCount, batchId }) => {
        const params = new URLSearchParams({
          resourceId,
          type,
          malPracticeCount,
        });

        if (batchId) {
          params.append("batchId", batchId);
        }

        return {
          url: `quiz/update-malPracticeCount?${params.toString()}`,
          method: "PATCH",
        };
      },
    }),
    approveReject: builder.mutation<
      ApproveRejectResponse,
      { assessmentId: string | number; status: string }
    >({
      query: ({ assessmentId, status }) => ({
        url: `assessment_template/approve?assessmentId=${assessmentId}&status=${status}`,
        method: "PATCH",
      }),
    }),
    getQuestionsForAssessment: builder.query<
      GetQuestionsForAssessmentResponse,
      QuestionsForQuizPayload
    >({
      query: ({
        parentId,
        parentType,
        difficultyLevel,
        questionSelectedType,
        pageNo,
        pageSize,
        batchId,
        isTournament,
        syllabusReference,
        questionType,
        questionFilter,
        traitIds,
        testType,
        isZupfly,
        paragraphId,
      }) => ({
        url: `/question/get?pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "POST",
        body: {
          parentId,
          parentType,
          difficultyLevel: difficultyLevel?.toUpperCase(),
          questionSelectedType,
          isTournament: isTournament ? true : false,
          ...(batchId && { batchId }),
          ...(syllabusReference && { syllabusId: syllabusReference }),
          ...(questionType && { questionType }),
          ...(questionFilter && { questionFilter }),
          ...(traitIds && traitIds?.length > 0 && { traitIds }),
          ...(testType && { testType }),
          ...(isZupfly !== undefined && { isZupfly }),
          ...(paragraphId && { paragraphId }),
        },
      }),
    }),
    getBatchAssessmentList: builder.query<
      GetBatchAssessmentListApiResponse,
      { batchId: string; pageNo: number; pageSize: number }
    >({
      query: ({ batchId, pageNo, pageSize }) => ({
        url: `/assessment/get-assessments-batch?batchId=${batchId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    getTrainerAssessmentList: builder.query<
      TrainerAssessmentApiResponse,
      { parentId: string; pageNo: number; pageSize: number }
    >({
      query: ({ parentId, pageNo, pageSize }) => ({
        url: `/assessment_template/get-assessmentTemplates-parent?parentId=${parentId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    getBatchAssessmentSummary: builder.query<
      GetBatchAssessmentResponse,
      { assessmentId: string }
    >({
      query: ({ assessmentId }) => ({
        url: `/assessment?assessmentId=${assessmentId}`,
        method: "GET",
      }),
    }),
    getDuplicatedAssessmentTemplate: builder.query<
      GetAssessmentDuplicateResponse,
      { assessmentName: string; subjectId: string }
    >({
      query: ({ assessmentName, subjectId }) => ({
        url: `assessment_template/check-duplicate?assessmentName=${assessmentName}&subjectId=${subjectId}`,
        method: "GET",
      }),
    }),
    getDuplicatedAssessment: builder.query<
      GetAssessmentDuplicateResponse,
      { assessmentName: string; batchId: string }
    >({
      query: ({ assessmentName, batchId }) => ({
        url: `assessment/check-duplicate?assessmentName=${assessmentName}&batchId=${batchId}`,
        method: "GET",
      }),
    }),
    getTrainerAssessmentBatchList: builder.query<
      GetTrainerAssessmentBatchListResponse,
      {
        assessmentId: string | null | undefined;
        trainerId: string;
        pageNo: number;
        pageSize: number;
        syllabusId: string;
      }
    >({
      query: ({ assessmentId, trainerId, pageNo, pageSize, syllabusId }) => ({
        url: `/batch/get-batches-assessment?assessmentId=${assessmentId}&trainerId=${trainerId}&pageNo=${pageNo}&pageSize=${pageSize}&syllabusId=${syllabusId}`,
        method: "GET",
      }),
    }),
    quizUrl: builder.query<QuizUrlRepsonse, { quizId: string }>({
      query: ({ quizId }) => ({
        url: `quiz/assign/${quizId}`,
        method: "GET",
      }),
    }),
    getStudentQuizReport: builder.query<
      QuizReportResponse,
      { quizId: string; page: number; pageSize: number }
    >({
      query: ({ quizId, page = 0, pageSize = 500 }) => ({
        url: `quiz/get-student-results?quizId=${quizId}&pageNo=${page}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    downloadQuizFile: builder.mutation<
      FileResponse,
      {
        payload: fileDownloadPayload;
        quizId?: string;
        isPayloadRequired?: boolean;
      }
    >({
      query: ({ payload, quizId, isPayloadRequired }) => {
        const url = `quiz/quiz-results-download?quizId=${quizId}`;

        const request: any = {
          url,
          method: "POST",
        };
        if (isPayloadRequired) {
          request.body = payload;
        }

        return request;
      },
    }),
    quizTableStructure: builder.mutation<
      QuizTableStructure,
      { quizId?: string }
    >({
      query: ({ quizId }) => {
        const url = `quiz/get-quiz?quizId=${quizId}`;
        return {
          url,
          method: "GET",
        };
      },
    }),
    getQuestionsByIDS: builder.mutation<
      GetQuestionsByIDS,
      { payload: string[] }
    >({
      query: ({ payload }) => ({
        url: `question/get-questions`,
        method: "POST",
        body: payload,
      }),
    }),
    getQuizzesMonthlyCount: builder.query<
      GetQuizzesMonthlyCountRespose,
      { organizationId: string; type: string }
    >({
      query: ({ organizationId, type }) => ({
        url: `quiz/get-quizzes-monthlyCount?organizationId=${organizationId}&type=${type}`,
        method: "GET",
      }),
      providesTags: ["getQuizCount"],
    }),
    updateAssessmentDates: builder.mutation<
      any,
      {
        assessmentId: string;
        batchId: string;
        startDate: string;
        endDate: string;
      }
    >({
      query: ({ assessmentId, batchId, endDate, startDate }) => ({
        url: `assessment/update`,
        method: "PATCH",
        params: { assessmentId, batchId, startDate, endDate },
      }),
    }),
    getPsychometricQuizResult: builder.query<
      GetPsychometricQuizResultResponse,
      {
        quizId: string; 
        page: number;
        pageSize: number;
      }
    >({
      query: ({ quizId, page, pageSize }) => ({
        url: `quiz/get-quiz-results?quizId=${quizId}&pageNo=${page}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    downloadPsychometricQuizResult: builder.query<
      FileResponse,
      { quizId: string }
    >({
      query: ({ quizId }) => ({
        url: `quiz/download-results?quizId=${quizId}`,
        method: "GET",
      }),
    }),
    downloadPsychometricQuizStudentReport: builder.query<
      FileResponse,
      { quizId: string; studentId: string }
    >({
      query: ({ quizId, studentId }) => ({
        url: `quiz/download-trait-report?quizId=${quizId}&studentId=${studentId}`,
        method: "GET",
      }),
    }),
    getStudentAssessmentList: builder.query<
      any,
      { batchId: string; parentId: string }
    >({
      query: ({ batchId, parentId }) => ({
        url: `assessment/get-student-assessments?batchId=${batchId}&parentId=${parentId}`,
        method: "GET",
      }),
    }),
    downloadIndividualAssessmentResult: builder.mutation<
      FileResponse,
      { assessmentId: string; batchId: string }
    >({
      query: ({ assessmentId, batchId }) => ({
        url: `assessment/assessment-results-download?assessmentId=${assessmentId}&batchId=${batchId}`,
        method: "POST",
      }),
    }),
    downloadIndividualStudentAssessmentReport: builder.mutation<
      FileResponse,
      { batchId: string; studentId: string }
    >({
      query: ({ batchId, studentId }) => ({
        url: `student/get-batch-assessments-download?batchId=${batchId}&studentId=${studentId}`,
        method: "GET",
      }),
    }),
    downloadCategoryReport: builder.query<
      FileResponse,
      { quizId: string }
    >({
      query: ({ quizId }) => ({
        url: `quiz/download-category-report?quizId=${quizId}`,
        method: "GET",
      }),
    }),
  }),
});
export const {
  useGetAllAssessmentQuery,
  useGetAssessmentSummaryQuery,
  useLazyGetAssessmentSummaryQuery,
  useGetBatchAssessmentSummaryQuery,
  useLazyGetBatchAssessmentSummaryQuery,
  useCreateAssessmentMutation,
  useAssignAssessmentMutation,
  useUpdateAssessmentMutation,
  useUpdateAssessmentTrainerMutation,
  useLazyGetQuestionsForAssessmentQuery,
  useGetBatchAssessmentListQuery,
  useLazyGetBatchAssessmentListQuery,
  useGetTrainerAssessmentListQuery,
  useGetTrainerAssessmentBatchListQuery,
  useGetUnassignedAssessmentQuery,
  useLazyGetDuplicatedAssessmentTemplateQuery,
  useLazyGetDuplicatedAssessmentQuery,
  useApproveRejectMutation,
  useQuizUrlQuery,
  useLazyGetStudentQuizReportQuery,
  useGetStudentQuizReportQuery,
  useDownloadQuizFileMutation,
  useQuizTableStructureMutation,
  useGetQuestionsByIDSMutation,
  useGetQuizzesMonthlyCountQuery,
  useUpdateMalPracticeMutation,
  useUpdateAssessmentDatesMutation,
  useLazyGetAllAssessmentQuery,
  useLazyGetTrainerAssessmentBatchListQuery,
  useLazyGetPsychometricQuizResultQuery,
  useLazyDownloadPsychometricQuizResultQuery,
  useLazyDownloadPsychometricQuizStudentReportQuery,
  useDownloadIndividualStudentAssessmentReportMutation,
  useGetAssessmentDashboardForAssessmentQuery,
  useLazyDownloadCategoryReportQuery,
  useDownloadIndividualAssessmentResultMutation,
  useLazyGetStudentAssessmentListQuery,
  useGetStudentAssessmentListQuery,
} = AssessmentApiSlice;
